import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { logoutUser } from '@/store/services/userServices';
import ProfileHeader from '@/containers/ProfileModule/ProfileHeader';
import { RoundButton } from '@/components/UI/RoundButton';
import {
  EMPTY_DASH,
  FUNDS_ORIGIN as FUNDS_ORIGIN_T,
  PLANNED_MONTHLY_INVESTMENT,
  VERIFF_CODES,
} from '@/helpers/constants';
import { INVESTOR_TYPES as INVESTOR_TYPES_T } from '@/components/Forms/InvestorTypeForm';
import { VerifyForm } from '@/components/VerifyForm';
import { APP_LINKS } from '@/helpers/links';
import { MODAL_TYPES } from '@/components/ModalProvider';
import { setModals } from '@/store/actions/commonActions';
import WarningBlock from '@/components/UI/WarningBlock';
import { WarningIcon, SuccessIcon } from '@/components/UI/Icons';
import { SuspendResidenceWarning } from '@/components/Warnings/SuspendResidenceWarning';
import Analytics from '@/analyticsGA';
import { useChat } from '@/hooks/useChat';
import { QUERY_KEYS } from '@/query/queryKeys';
import { ChangeEmailModal } from '@/modals/ChangeEmailModal';
import { TwoFactorAuthModal } from '@/modals/TwoFactorAuthModal';
import { AccountDelete } from './components/AccountDelete';
import { useGetMe } from '@/hooks/useGetMe';
import { useVeriffStatus } from '@/hooks/shared/useVeriffStatus';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { useModal } from '@/providers/modal';
import { ReCaptchaProvider } from '@/providers/recaptcha';
import './index.scss';

const ProfilePage = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const { openSingleModal, closeModal } = useModal();
  useChat(false);

  const { profile: user, isSuspended, isVerified, isPhoneVerified } = useGetMe();
  const { ready, veriffCode } = useVeriffStatus();

  const FUNDS_ORIGIN = FUNDS_ORIGIN_T(t);
  const INVESTOR_TYPES = INVESTOR_TYPES_T(t);

  const preSavedForms = useRef({});

  useEffect(() => {
    Analytics.pageNavigation('Profile');
  }, []);

  const onAvatarEdit = () =>
    dispatch(setModals({ type: MODAL_TYPES.profile_avatar }));

  const onProfileEdit = () =>
    dispatch(
      setModals({
        type: MODAL_TYPES.profile_detail,
        preSaved: preSavedForms.current[MODAL_TYPES.profile_detail],
        onOutsideClick: formValues => {
          preSavedForms.current[MODAL_TYPES.profile_detail] = formValues;
        },
      }),
    );
  const onPersonalEdit = () =>
    dispatch(
      setModals({
        type: MODAL_TYPES.profile_personal,
        preSaved: preSavedForms.current[MODAL_TYPES.profile_personal],
        onOutsideClick: formValues => {
          preSavedForms.current[MODAL_TYPES.profile_personal] = formValues;
        },
      }),
    );

  const onInvestorTypeEdit = () =>
    dispatch(
      setModals({
        type: MODAL_TYPES.investor_type,
        preSaved: preSavedForms.current[MODAL_TYPES.investor_type],
        onOutsideClick: formValues => {
          preSavedForms.current[MODAL_TYPES.investor_type] = formValues;
        },
      }),
    );

  const onPhoneVerify = () => {
    openSingleModal(MODAL_IDS.two_factor_auth_modal);
  };

  return (
    <div className="profile_page">
      <div className="container-inner">
        <ProfileHeader onPhotoChange={onAvatarEdit} />

        {isSuspended ? (
          <SuspendResidenceWarning className="profile_page__suspend" />
        ) : (
          !isVerified && (
            <WarningBlock className="profile_page__warning" withBorder>
              <div className="f-16 f-400">
                {t('provide_details_to_start_investing')}
              </div>
            </WarningBlock>
          )
        )}

        <section className="profile_page__details">
          <div className="profile_page__section_header">
            <h2 className="f-26 f-500">{t('profile_details')}</h2>
          </div>

          <div className="profile_page__box">
            <div className="profile_page__box_col">
              <div className="profile_page__first_name">
                <div className="f-14 f-400">{t('first_name')}</div>
                <div className="f-16 f-400">
                  {user?.first_name || EMPTY_DASH}
                </div>
              </div>
              <div className="profile_page__last_name">
                <div className="f-14 f-400">{t('last_name')}</div>
                <div className="f-16 f-400">
                  {user?.last_name || EMPTY_DASH}
                </div>
              </div>
            </div>
            <div className="profile_page__box_col">
              <div className="profile_page__company">
                <div className="f-14 f-400">{t('company')}</div>
                <div className="f-16 f-400">
                  {user?.investor?.company_name || EMPTY_DASH}
                </div>
              </div>
              <div className="profile_page__position">
                <div className="f-14 f-400">{t('position')}</div>
                <div className="f-16 f-400">
                  {user?.investor?.position || EMPTY_DASH}
                </div>
              </div>
            </div>
            <div className="profile_page__box_col">
              {user && (
                <RoundButton
                  label={t('edit_profile_details')}
                  onClick={onProfileEdit}
                  fullWidth
                  className="profile_page__edit"
                />
              )}
            </div>
          </div>
        </section>

        <section className="profile_page__personal" id="phone_verification">
          <div className="profile_page__section_header">
            <h2 className="f-26 f-500">{t('personal_information')}</h2>
          </div>

          <div className="profile_page__box">
            <div className="profile_page__box_col">
              <div className="profile_page__phone">
                <div className="f-14 f-400">{t('phone_number')}</div>
                <div className="profile_page__phone_value">
                  <div className="f-16 f-400">{user?.phone || EMPTY_DASH}</div>
                  {isPhoneVerified && (
                    <SuccessIcon className="profile_page__phone_verified" />
                  )}
                  {user?.phone &&
                    !isPhoneVerified &&
                    !['RU', 'BY'].includes(user?.country_code) && (
                      <RoundButton
                        className="profile_page__phone_verify"
                        label={() => t('verify')}
                        fillBackground
                        onClick={onPhoneVerify}
                      />
                    )}
                </div>
              </div>
              <div className="profile_page__citizenship">
                <div className="f-14 f-400">{t('citizenship')}</div>
                <div className="f-16 f-400">
                  {user?.citizenship || EMPTY_DASH}
                </div>
              </div>
              <div className="profile_page__funds_origin">
                <div className="f-14 f-400">{t('origin_of_funds')}</div>
                <div className="f-16 f-400">
                  {FUNDS_ORIGIN[user?.investor?.origin_of_funds] || EMPTY_DASH}
                </div>
              </div>
              <div className="profile_page__investment">
                <div className="f-14 f-400">
                  {t('planned_monthly_investment')}
                </div>
                <div className="f-16 f-400">
                  {PLANNED_MONTHLY_INVESTMENT[
                    user?.investor?.monthly_investment
                  ] || EMPTY_DASH}
                </div>
              </div>
            </div>
            <div className="profile_page__box_col">
              <div className="profile_page__country">
                <div className="f-14 f-400">{t('residence_country')}</div>
                <div className="f-16 f-400">{user?.country || EMPTY_DASH}</div>
              </div>
              <div className="profile_page__city">
                <div className="f-14 f-400">{t('residence_city')}</div>
                <div className="f-16 f-400">{user?.city || EMPTY_DASH}</div>
              </div>
              <div className="profile_page__address">
                <div className="f-14 f-400">{t('residence_address')}</div>
                <div className="f-16 f-400">{user?.address || EMPTY_DASH}</div>
              </div>
              <div className="profile_page__postal">
                <div className="f-14 f-400">{t('residence_postal_code')}</div>
                <div className="f-16 f-400">
                  {user?.postal_code || EMPTY_DASH}
                </div>
              </div>
            </div>
            <div className="profile_page__box_col">
              {user && (
                <RoundButton
                  label={t('edit_personal_information')}
                  onClick={onPersonalEdit}
                  fullWidth
                  className="profile_page__edit"
                />
              )}
            </div>
          </div>
        </section>

        {user?.country_code === 'GB' && (
          <section className="profile_page__investor_type">
            <div className="profile_page__section_header">
              <h2 className="f-26 f-500">{t('type_of_investor')}</h2>
            </div>

            <div className="profile_page__investor_type_container">
              <div className="profile_page__investor_type_content">
                {user.investor.investor_type ? (
                  <>
                    <h2 style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                      {INVESTOR_TYPES[user.investor.investor_type].title}
                    </h2>
                    <p className="f-16 f-400">
                      {INVESTOR_TYPES[user.investor.investor_type].description}
                    </p>
                  </>
                ) : (
                  <div className="profile_page__investor_type_empty">
                    {t('select_your_type_of_investor')}
                  </div>
                )}
              </div>
              <div className="profile_page__investor_type_edit">
                <RoundButton
                  label={t('edit')}
                  fullWidth
                  onClick={onInvestorTypeEdit}
                  className="profile_page__edit"
                />
              </div>
            </div>
          </section>
        )}

        {ready && user && !isSuspended && (
          <section className="profile_page__veriff">
            <div className="profile_page__section_header">
              <h2 className="f-26 f-500">
                {veriffCode === VERIFF_CODES.verified
                  ? t('identity_verification')
                  : t('verify_your_identity')}
              </h2>
              {veriffCode !== VERIFF_CODES.verified && <WarningIcon />}
            </div>
            <div className="profile_page__box">
              <div className="profile_page__box_col">
                {veriffCode === VERIFF_CODES.verified ? (
                  <div className="profile_page__veriff_status">
                    <div className="f-14 f-400">{t('status')}</div>
                    <div className="f-16 f-400">
                      {t('identity_verified_successfully')}
                    </div>
                  </div>
                ) : VERIFF_CODES.submitted === veriffCode ? (
                  <div className="profile_page__veriff_status">
                    <div className="f-14 f-400">{t('status')}</div>
                    <div className="f-16 f-400">
                      {t('identity_being_verified')}
                    </div>
                  </div>
                ) : (
                  <div className="profile_page__veriff_form">
                    <VerifyForm
                      source="profile"
                      onSuccess={() => {
                        queryClient.setQueryData(
                          [QUERY_KEYS.user.veriff_status],
                          () => ({ code: VERIFF_CODES.submitted }),
                        );
                        closeModal(MODAL_IDS.country_verification)
                        navigate(APP_LINKS.dashboard);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        <section className="profile_page__buttons">
          <RoundButton
            label={t('log_out')}
            onClick={logoutUser}
            fullWidth
            className="profile_page__logout"
          />
        </section>
        <AccountDelete />
        <ChangeEmailModal />
      </div>
      <ReCaptchaProvider>
        <TwoFactorAuthModal />
      </ReCaptchaProvider>
    </div>
  );
};

export default ProfilePage;
